import Loader from 'components/ui/LoadingIndicator';
import { useColorScheme, useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Dialog,
    DialogActions as DialogFooter,
    DialogContent,
    DialogTitle,
    Paper,
    Typography,
} from '@mui/material';
import { AlertBox } from 'components/ui/Feedback';
import React, { useState, useEffect, Fragment, useMemo } from 'react';

import { Grid } from '@mui/material';
import {
    Elements,
    PaymentElement,
    ElementsConsumer,
} from '@stripe/react-stripe-js';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import {
    createStripeFormAppearance,
    stripeCustomFont,
    stripeFormOptions,
    stripePromise,
} from '../../utils/stripe';
import {
    StripePaymentSetupIntent,
    CREATE_STRIPE_PAYMENT_SETUP_INTENT,
} from '../../gql/users';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { baseUrls } from '../../utils/baseUrls.utils';
import { ISO_COUNTRIES } from '../../gql/countries';
import { IsoCountry } from '../../reference-data/country-iso-codes';
import { getConcretePalette } from '../../utils/theme.utils';

function filter(array: (string | undefined)[]) {
    return array.filter((t) => !!t) as string[];
}

interface UpdateCardDialogProps {
    children?: React.ReactNode;
    subscriptionId: string;
    isDialogOpen: boolean;
    openDialog?: () => void;
    closeDialog: () => void;
    onStripeCardUpdated: () => void;
}

const UpdateCardDialog: React.FC<UpdateCardDialogProps> = (props) => {
    const { isDialogOpen, closeDialog, subscriptionId, onStripeCardUpdated } =
        props;
    const theme = useTheme();
    const { mode, systemMode } = useColorScheme();
    const palette = getConcretePalette(theme, mode, systemMode);
    const [stripe, setStripe] = useState<Stripe | null>();
    const [elements, setElements] = useState<StripeElements | null>();
    const [busy, setBusy] = useState(false);
    const [stripeError, setStripeError] = useState<string | undefined>();
    const redirectUrl = `${window.location.origin}/insights${baseUrls.subscriptions}?channels`;
    const { data: isoCountriesResponse } = useQuery<{
        isoCountries: IsoCountry[];
    }>(ISO_COUNTRIES, { fetchPolicy: 'cache-first' });
    const stripeFormAppearance = useMemo(
        () => createStripeFormAppearance(theme, palette),
        [theme, palette]
    );

    useEffect(() => {
        if (isDialogOpen) {
            setBusy(false);
        }
    }, [isDialogOpen]);

    const [
        stripeSetupIntent,
        { data: setupIntentResponse, loading: setupIntentResponseLoading },
    ] = useMutation<StripePaymentSetupIntent>(
        CREATE_STRIPE_PAYMENT_SETUP_INTENT
    );

    const handlePaymentElementReady = (
        stripe: Stripe | null,
        elements: StripeElements | null
    ) => {
        setStripe(stripe);
        setElements(elements);
    };

    const handleSubmit = async (event: { preventDefault: () => void }) => {
        setBusy(true);
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        let billingAddress;
        const setupIntent = setupIntentResponse?.createPaymentSetupIntent;

        if (setupIntent?.billingAddress) {
            const {
                // eslint-disable-next-line
                city,
                country,
                line1,
                line2,
                postalCode: postal_code,
                state,
            } = setupIntent.billingAddress;
            // eslint-disable-next-line
            billingAddress = {
                city,
                country,
                line1,
                line2,
                postal_code,
                state,
            };
        }

        const { error } = await stripe.confirmSetup({
            elements: elements as any,
            redirect: 'if_required',
            confirmParams: {
                // eslint-disable-next-line
                return_url: redirectUrl,
                // eslint-disable-next-line
                payment_method_data: {
                    // eslint-disable-next-line
                    billing_details: {
                        address: billingAddress,
                    },
                },
            },
        });

        setBusy(false);

        if (error) {
            setStripeError(error?.message);
        } else {
            onStripeCardUpdated();
            closeDialog();
        }
    };

    useEffect(() => {
        if (setupIntentResponse) return;
        if (!isDialogOpen) return;
        stripeSetupIntent({ variables: { subscriptionId: subscriptionId } });
    }, [setupIntentResponse, stripeSetupIntent, isDialogOpen, subscriptionId]);

    const billingAddress =
        setupIntentResponse?.createPaymentSetupIntent.billingAddress;
    const metadata = setupIntentResponse?.createPaymentSetupIntent.metadata;
    const country = isoCountriesResponse?.isoCountries.find(
        (c) => c.alpha2 === billingAddress?.country
    )?.name;

    const displayBillingAddress = filter([
        filter([billingAddress?.line1, billingAddress?.line2]).join(', '),
        filter([billingAddress?.city, billingAddress?.postalCode]).join(', '),
        filter([
            billingAddress?.state,
            country || billingAddress?.country,
        ]).join(', '),
    ]).map((x, i) => (
        <Fragment key={i}>
            {x}
            <br />
        </Fragment>
    ));

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={isDialogOpen}
            slotProps={{
                paper: { sx: { maxWidth: 500 } },
            }}
        >
            <DialogTitle>Update payment details</DialogTitle>
            {stripeError && (
                <Box
                    sx={{
                        p: 2,
                        pt: 0,
                    }}
                >
                    <AlertBox
                        visible={!!stripeError}
                        styleName="error"
                        onClose={() => setStripeError('')}
                    >
                        {stripeError}
                    </AlertBox>
                </Box>
            )}
            <DialogContent>
                <Box sx={{ width: { xs: '100%', sm: 468 }, minHeight: 120 }}>
                    {!setupIntentResponse && setupIntentResponseLoading ? (
                        <Loader minHeight={120} />
                    ) : (
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <Elements
                                    stripe={stripePromise}
                                    options={{
                                        clientSecret:
                                            setupIntentResponse
                                                ?.createPaymentSetupIntent
                                                .clientSecret,
                                        fonts: stripeCustomFont,
                                        appearance: stripeFormAppearance,
                                    }}
                                >
                                    <ElementsConsumer>
                                        {({ stripe, elements }) => (
                                            <PaymentElement
                                                options={stripeFormOptions}
                                                onReady={() =>
                                                    handlePaymentElementReady(
                                                        stripe,
                                                        elements
                                                    )
                                                }
                                            />
                                        )}
                                    </ElementsConsumer>
                                </Elements>
                            </Grid>
                            <Grid size={12}>
                                <Paper
                                    elevation={0}
                                    sx={{ bgcolor: 'background.paper', p: 2 }}
                                >
                                    <Grid container spacing={2}>
                                        <Grid size={12}>
                                            <Typography variant="subtitle1">
                                                Billing information
                                            </Typography>
                                        </Grid>
                                        <Grid size={12}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: 'flex-start',
                                                    textAlign: 'right',
                                                }}
                                            >
                                                <Typography variant="caption">
                                                    <strong>
                                                        Business name
                                                    </strong>
                                                </Typography>
                                                <Typography variant="caption">
                                                    {metadata?.businessName}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={12}>
                                            <Box
                                                sx={{
                                                    height: 1,
                                                    bgcolor: 'divider',
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: 'flex-start',
                                                    textAlign: 'right',
                                                }}
                                            >
                                                <Typography variant="caption">
                                                    <strong>Tax ID</strong>
                                                </Typography>
                                                <Typography variant="caption">
                                                    {metadata?.taxId}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={12}>
                                            <Box
                                                sx={{
                                                    height: 1,
                                                    bgcolor: 'divider',
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: 'flex-start',
                                                    textAlign: 'right',
                                                }}
                                            >
                                                <Typography variant="caption">
                                                    <strong>
                                                        Billing address
                                                    </strong>
                                                </Typography>
                                                <Typography variant="caption">
                                                    {displayBillingAddress}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </DialogContent>
            <DialogFooter>
                <Button
                    id="updatecard-dialog-cancel-button"
                    variant="outlined"
                    color="inherit"
                    onClick={closeDialog}
                >
                    {'Cancel'}
                </Button>
                <Button
                    id="updatecard-dialog-submit-button"
                    variant="contained"
                    disabled={busy}
                    onClick={handleSubmit}
                >
                    {'Update and save'}
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

export default UpdateCardDialog;
